import {useCallback} from 'react';
import assert from 'assert-ts';
import {Concept} from 'types';
import {EntitySearchState} from 'feature/linkedValueSearch/context/types';
import type {LinkedValueSearchPageConfig} from 'sceneExtensions/extensions/linkedValueSearch/types';
import {FieldExtensionEnum} from 'sceneExtensions/types';
import type {VerifiedLinkedCatalogPostLink} from 'schemaDefinition/types';
import type {WorkMetadata, WorkV4} from 'services/metadata/types';
import {metadataEditNewChangeRequestFromCopy} from 'services/metadata';
import {useAppStore} from 'store/hooks';
import {copyFromOtherWork} from '../functions';

export const useLinkedValueSearchPageConfig = (
  work: Pick<WorkV4, 'id' | 'preferredTitle'>,
): LinkedValueSearchPageConfig => {
  const {dispatch, getState} = useAppStore();

  const handleCopy = useCallback(
    (targetWork: VerifiedLinkedCatalogPostLink<WorkMetadata>) => {
      const dummyManifestation = assert(
        getState().metadataEdit.savedMetadata?.manifestations[0],
      );
      const manifestationInfo = {
        toManifestationPlaceholder: {
          id: dummyManifestation.id,
          expressionId: dummyManifestation.expressionId,
        } as const,
      };

      const changeRequest = copyFromOtherWork({
        toWork: work,
        ...manifestationInfo,
        fromWork: assert(
          targetWork.entityValue?.work,
          'useMoveToOrCopyFromOtherWork: fromWork expected',
        ),
      });
      if (changeRequest) {
        dispatch(metadataEditNewChangeRequestFromCopy(changeRequest));
      }
      return Promise.resolve();
    },
    [dispatch, getState, work],
  );

  return {
    type: FieldExtensionEnum.linkedValueSearch,
    sourceField: {
      fieldId: 'searchPanel',
      fieldConfig: {
        titleKey: 'general.search',
        initialState: EntitySearchState.search,
        entity: Concept.work,
        sourceEntityId: work.id,
        canCopy: true,
        allowedEntities: [Concept.work],
      },
      currentValue: {
        linkStatus: 'verified',
        entityId: work.id,
        type: Concept.work,
        name: work?.preferredTitle?.[0] ?? '',
      },
    },
    setSourceValue: (target, action, _keepOpen) => {
      const targetWork = target as
        | VerifiedLinkedCatalogPostLink<WorkMetadata>
        | undefined;
      if (targetWork) {
        if (action === 'copy') {
          handleCopy(targetWork);
        } else {
          assert(false, 'useLinkedValueSearchPageConfig: Unexpected action', {
            action,
          });
        }
      }
    },
  };
};
